import { useDrag } from "@use-gesture/react";
import classNames from "classnames";
import { action, computed, entries, observable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useRef } from "react";
import { atom } from "../atom";
import { Position } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import { canvasViewport, canvasViewportShouldAnimateAtom } from "./CanvasState";
import {
  OVERLAY_PANE_CLASSNAME,
  OVERLAY_PANE_HEADER_BUTTON_CLASSNAME,
  OVERLAY_PANE_HEADER_CLASSNAME,
} from "./SandboxUtils";

export const showViewportMarksPanelAtom = atom(false);
type CanvasViewMark = {
  offset: Position;
  zoom: number;
};
export const canvasViewMarksMobx = observable.map<string, CanvasViewMark>(
  new Map()
);
function addCurrentViewAsMark(markKey: string) {
  runInAction(() => {
    canvasViewMarksMobx.set(markKey, {
      offset: canvasViewport.offset,
      zoom: canvasViewport.zoom,
    });
  });
}

const ALPHABET = "abcdefghijklmnopqrstuvwxyz".split("");
const MENU_ITEM_CLASSNAME =
  "px-2 py-2 flex items-center gap-1.5 text-left text-neutral-500 w-full hover:bg-primary-50 transition";

const MarkButton = observer(
  ({ markKey, viewport }: { markKey: string; viewport: CanvasViewMark }) => {
    const isSelected = computed(
      () =>
        canvasViewport.offset[0] === viewport.offset[0] &&
        canvasViewport.offset[1] === viewport.offset[1] &&
        canvasViewport.zoom === viewport.zoom
    ).get();
    return (
      <button
        onClick={action(() => {
          if (
            canvasViewport.offset !== viewport.offset ||
            canvasViewport.zoom !== viewport.zoom
          ) {
            canvasViewportShouldAnimateAtom.value = true;
            canvasViewport.offset = viewport.offset;
            canvasViewport.zoom = viewport.zoom;
          }
        })}
        className={MENU_ITEM_CLASSNAME}
      >
        <FeatherIcon
          icon="map-pin"
          size="sm"
          className={classNames(
            isSelected ? "text-accent-400 opacity-100" : "opacity-50"
          )}
        />
        mark {markKey}
      </button>
    );
  }
);

const ViewportMarksPanelImpl = observer(() => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const offsetRef = useRef<Position>([0, 0]);

  const bindDrag = useDrag(
    ({ target, first, last, offset }) => {
      offsetRef.current = offset;
      rootRef.current!.style.transform = `translate(${offset[0]}px, ${offset[1]}px)`;
      if (first && target instanceof HTMLElement) {
        target.style.cursor = "grabbing";
      }
      if (last && target instanceof HTMLElement) {
        target.style.cursor = "";
      }
    },
    { from: () => offsetRef.current }
  );
  return (
    <div
      className={classNames(
        OVERLAY_PANE_CLASSNAME,
        "bg-neutral-0 fixed top-16 right-4 min-w-[12rem] flex flex-col rounded overflow-hidden shadow-xl transform z-1"
      )}
      ref={rootRef}
    >
      <div
        {...bindDrag()}
        className={classNames(
          OVERLAY_PANE_HEADER_CLASSNAME,
          "h-8 px-2 flex items-center cursor-grab touch-none"
        )}
      >
        <button
          onClick={action(() => {
            showViewportMarksPanelAtom.value = false;
          })}
          className={classNames(OVERLAY_PANE_HEADER_BUTTON_CLASSNAME, "mr-2")}
        >
          <FeatherIcon icon="x" size="xs" />
        </button>
        <div className="font-medium">viewport marks</div>
        <div className="grow" />
        <Tooltip content="clear marks">
          <button
            onClick={action(() => {
              canvasViewMarksMobx.clear();
            })}
            className="flex p-0.5 -mr-1 border rounded-sm border-neutral-0 border-opacity-0 hover:border-opacity-50"
          >
            <FeatherIcon icon="trash" size="sm" />
          </button>
        </Tooltip>
      </div>
      <div>
        {entries(canvasViewMarksMobx).map(([markKey, viewport], i) => (
          <div className="border-b border-neutral-150" key={i}>
            <MarkButton markKey={markKey} viewport={viewport} />
          </div>
        ))}
        <div>
          <button
            onClick={() => {
              for (const c of ALPHABET) {
                if (!canvasViewMarksMobx.has(c)) {
                  addCurrentViewAsMark(c);
                  break;
                }
              }
            }}
            className={MENU_ITEM_CLASSNAME}
          >
            <FeatherIcon icon="plus" size="sm" className="opacity-50" />
            add current viewport mark
          </button>
        </div>
      </div>
    </div>
  );
});

export const ViewportMarksPanel = observer(() => {
  if (!showViewportMarksPanelAtom.value) {
    return null;
  }
  return <ViewportMarksPanelImpl />;
});
